import React from "react";
import Spinner from "@atlas/uicorn/lib/Display/Spinner";
import Text from "@atlas/uicorn/lib/Display/Text";
import styles from "../../../styles/composer-attachments.module.css";
import AttachmentPreview from "./attachment-preview";
import { TAttachment } from "@atlas/tool/box/file/types";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";
import CrossIcon from "@atlas/uicorn/lib/Icon/CrossIcon";
import Link from "next/link";
import useRefCallback from "use-ref-callback";

const ComposerAttachments = React.memo(function ComposerAttachments(
  props: TComposerAttachmentsProps
): React.ReactElement | null {
  const { attachments, disabled, uploading, onRemove, ...elementProps } = props;

  const handleRemove = useRefCallback((event, attachment) => {
    event.preventDefault();
    onRemove && onRemove(attachment);
  });

  if (!attachments.length && !uploading) return null;

  return (
    <div {...elementProps} className={styles.composerAttachments}>
      {attachments.map((a) => (
        <Link
          key={a.url}
          href={a.url}
          target="_blank"
          className={styles.attachment}
        >
          <AttachmentPreview key={a.url} attachment={a} />
          <IconButton
            className={styles.removeButton}
            onClick={(event) => handleRemove(event, a)}
            icon={<CrossIcon size={10} />}
            tooltip="Remove"
          />
        </Link>
      ))}
      {uploading && uploading > 0 ? (
        <span className={styles.uploadingStatus}>
          <Spinner />
          <Text>
            {uploading} {uploading > 1 ? "files are" : "file is"} uploading
          </Text>
        </span>
      ) : null}
    </div>
  );
});

export default ComposerAttachments;

export type TComposerAttachmentsProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  attachments: TAttachment[];
  disabled?: boolean;
  onRemove?: (attachment: TAttachment) => void;
  uploading?: number;
};
